import { useTranslation } from 'react-i18next';
import { BodyText, Card, MaterialSymbol, withStyles } from 'vopay';
import type { AccountUserRole } from 'vopay';
import { Skeleton, Title, UtilButton } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import styles from './UserRolesContainer.module.scss';

const cn = withStyles(styles);

type UserRolesContainerProps = {
  onCreateRoleClick: () => void;
  onRoleClick?: (role: AccountUserRole) => void;
  roles: AccountUserRole[];
  fetching?: boolean;
};

export default function UserRolesContainer({ onCreateRoleClick, onRoleClick, roles, fetching }: UserRolesContainerProps) {
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserRoles' });

  if (!permissions.ViewUserRoles && !permissions.ManageUserRoles) {
    return null;
  }

  if (fetching) {
    return (
      <Card hoverEffect={false} className={cn('container')}>
        <Skeleton height="80px" />
      </Card>
    );
  }

  const canManage = !!permissions.ManageUserRoles;
  const hasRoles = roles.length > 0;

  // View-only users with no roles to look at get nothing — the empty-state
  // "Create Roles" CTA doesn't apply to them.
  if (!hasRoles && !canManage) {
    return null;
  }

  if (hasRoles) {
    return (
      <Card hoverEffect={false} className={cn('container')}>
        <div className={cn('header')}>
          <Title level="5" className={cn('headerTitle')}>
            {t('populated.title', { defaultValue: 'Roles' })}
          </Title>
          {canManage && (
            <UtilButton size="medium" iconLeft="add" onClick={onCreateRoleClick}>
              {t('actions.createRole', { defaultValue: 'Create Roles' })}
            </UtilButton>
          )}
        </div>
        <div className={cn('roleCards')}>
          {roles.map((role) => (
            <button
              key={role.RoleID}
              type="button"
              className={cn('roleCard')}
              onClick={() => onRoleClick?.(role)}
            >
              <span className={cn('roleCardName')}>{role.Name}</span>
              <span className={cn('roleCardCount')}>0</span>
            </button>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card hoverEffect={false} className={cn('container')}>
      <div className={cn('header')}>
        <Title level="5" className={cn('headerTitle')}>
          {t('emptyState.title', { defaultValue: 'Create Roles' })}
        </Title>
        {canManage && (
          <UtilButton size="medium" iconLeft="add" onClick={onCreateRoleClick}>
            {t('actions.createRole', { defaultValue: 'Create Roles' })}
          </UtilButton>
        )}
      </div>
      <div className={cn('emptyContent')}>
        <MaterialSymbol icon="groups" size="40px" className={cn('icon')} />
        <BodyText weight="md" className={cn('description')}>
          {t('emptyState.description', {
            defaultValue:
              'Roles let you group permissions and assign them to users in one step. Create your first role to get started.',
          })}
        </BodyText>
      </div>
    </Card>
  );
}
